import { api } from "./client";
import type { ServiceEstimate, EstimateItem, EstimateSummary } from "@/lib/types";

interface EstimateListResponse {
  estimates: ServiceEstimate[];
  total: number;
}

interface EstimateFilters {
  clinicId?: string;
  vehicleId?: string;
  status?: string;
  isInsuranceClaim?: boolean;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

interface CreateEstimateData {
  vehicleId: string;
  appointmentId?: string;
  documentType?: 'ESTIMATE' | 'SERVICE_ORDER';
  description?: string;
  damageType?: string;
  severity?: string;
  fuelLevel?: string;
  estimatedDays?: number;
  startDate?: string;
  expectedEnd?: string;
  isInsuranceClaim?: boolean;
  insuranceClaimNumber?: string;
  internalNotes?: string;
  customerNotes?: string;
}

interface CreateItemData {
  type: string;
  description: string;
  quantity?: number;
  unitPrice: number;
  partNumber?: string;
  partBrand?: string;
  isOriginal?: boolean;
  laborHours?: number;
  notes?: string;
}

interface EstimatePdfData {
  estimate: {
    number: string;
    date: string;
    status: string;
    description?: string;
    damageType?: string;
    severity?: string;
    estimatedDays?: number;
  };
  vehicle: {
    plate: string;
    brand: string;
    model: string;
    year: number;
    color: string;
  };
  customer: {
    name: string;
    phone?: string;
    email?: string;
    cpf?: string;
  };
  items: Array<{
    type: string;
    description: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    partNumber?: string;
    partBrand?: string;
    isOriginal?: boolean;
  }>;
  totals: {
    labor: number;
    parts: number;
    paint: number;
    other: number;
    discount: number;
    total: number;
  };
  insurance?: {
    company?: string;
    claimNumber?: string;
    approved?: boolean;
  };
  notes?: string;
}

export const estimatesService = {
  list: (filters?: EstimateFilters) =>
    api.get<EstimateListResponse>("/estimates", filters),

  getByNumber: (number: string) =>
    api.get<ServiceEstimate>(`/estimates/number/${number}`),

  getById: (id: string) =>
    api.get<ServiceEstimate>(`/estimates/${id}`),

  create: (data: CreateEstimateData) =>
    api.post<ServiceEstimate>("/estimates", data),

  // Items
  addItem: (estimateId: string, data: CreateItemData) =>
    api.post<EstimateItem>(`/estimates/${estimateId}/items`, data),

  updateItem: (itemId: string, data: Partial<CreateItemData>) =>
    api.put<EstimateItem>(`/estimates/items/${itemId}`, data),

  removeItem: (itemId: string) =>
    api.delete(`/estimates/items/${itemId}`),

  updateItemStatus: (itemId: string, status: string) =>
    api.patch<EstimateItem>(`/estimates/items/${itemId}/status`, { status }),

  // Estimate operations
  applyDiscount: (id: string, discount: number) =>
    api.post<ServiceEstimate>(`/estimates/${id}/discount`, { discount }),

  updateStatus: (id: string, status: string, data?: {
    approvedBy?: string;
    startDate?: string;
    expectedEnd?: string;
  }) =>
    api.patch<ServiceEstimate>(`/estimates/${id}/status`, { status, ...data }),

  // Analytics
  getSummary: (startDate?: string, endDate?: string) =>
    api.get<EstimateSummary>("/estimates/summary", { startDate, endDate }),

  getPdfData: (id: string) =>
    api.get<EstimatePdfData>(`/estimates/${id}/pdf`),

  clone: (id: string) =>
    api.post<ServiceEstimate>(`/estimates/${id}/clone`),
};
